import React, { useState } from 'react';
import {
  BookOpen,
  Plus,
  Pin,
  PinOff,
  ChevronLeft,
  ChevronRight,
  Check,
  Undo2,
  Redo2,
  GripVertical,
  MapPin,
  MessageCircle,
  Edit,
  X,
  Clock,
  PlayCircle,
  Flag,
  Trash2,
} from 'lucide-react';
import {
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { TutorialEngine } from '@/components/tutorial/TutorialEngine';
import { cn } from '@/lib/utils';

const WEEK_DAYS = [
  { name: 'Domingo', letter: 'D' },
  { name: 'Lunes', letter: 'L' },
  { name: 'Martes', letter: 'M' },
  { name: 'Miércoles', letter: 'X' },
  { name: 'Jueves', letter: 'J' },
  { name: 'Viernes', letter: 'V' },
  { name: 'Sábado', letter: 'S' },
];

const DISPLAY_WEEK_DAYS = [
  WEEK_DAYS[1],
  WEEK_DAYS[2],
  WEEK_DAYS[3],
  WEEK_DAYS[4],
  WEEK_DAYS[5],
  WEEK_DAYS[6],
  WEEK_DAYS[0],
];
function formatDateKey(date) {
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = String(date.getFullYear());
  return `${yyyy}-${mm}-${dd}`;
}

function mondayOf(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
}

function getWeekDates(mondayAnchor) {
  const dates = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(mondayAnchor);
    d.setDate(d.getDate() + i);
    dates.push(d);
  }
  return dates;
}

function buildToday() {
  const d = new Date();
  const day = WEEK_DAYS[d.getDay()];
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return {
    name: day.name,
    letter: day.letter,
    dateLabel: `${day.name} · ${dd}/${mm}/${yy}`,
    date: d,
    dateKey: formatDateKey(d),
  };
}

function makeInitialDraft(dayName) {
  return {
    name: '',
    whatsapp: '',
    locationUrl: '',
    count: '1',
    isFixed: true,
    days: [dayName],
  };
}

const FIELD_ORDER = ['name', 'whatsapp', 'maps', 'count', 'fixed', 'days'];

const STEPS = [
  { id: 'plus', target: 'plus', message: 'Pulsa + para añadir un cliente.' },
  { id: 'name', target: 'name', message: 'Escribe el nombre del cliente.' },
  { id: 'whatsapp', target: 'whatsapp', message: 'Añade el número o enlace del grupo de WhatsApp.' },
  { id: 'maps', target: 'maps', message: 'Añade la localización de Google Maps o omite este paso.' },
  { id: 'count', target: 'count', message: 'Indica cuántos clientes representa esta entrada.' },
  { id: 'fixed', target: 'fixed', message: 'Indica si este cliente se repetirá semanalmente.' },
  { id: 'days', target: 'days', message: 'Selecciona los días en los que vendrá este cliente.' },
  { id: 'save', target: 'save', message: 'Pulsa Añadir Cliente para guardarlo.' },
  { id: 'result', target: 'list', message: 'Perfecto. El cliente ya aparece en la lista.' },
  { id: 'row-maps', target: 'row-maps', message: 'Pulsa aquí para acceder directamente a la localización del cliente en Google Maps.' },
  { id: 'row-whatsapp', target: 'row-whatsapp', message: 'Pulsa aquí para acceder directamente a su WhatsApp o grupo.' },
  { id: 'row-edit', target: 'row-edit', message: 'Pulsa aquí para editar la información del cliente.' },
  { id: 'edit-data', target: 'edit-data', message: 'Estos son los datos que ya guardaste.' },
  { id: 'note-text', target: 'note-text', message: 'Escribe una nota que quieras tener visible para este cliente.' },
  { id: 'note-days', target: 'note-days', message: 'Selecciona los días en los que quieres mostrar esta nota.' },
  { id: 'note-week', target: 'note-week', message: 'Actívalo si quieres que esta nota se aplique únicamente esta semana.' },
  { id: 'cancel', target: 'cancel', message: 'Marca aquí los días en los que este cliente no vendrá.' },
  { id: 'cancel-navigation', target: 'cancel-navigation', message: 'Puedes cambiar de semana para cancelar también días anteriores o futuros.' },
  { id: 'save-edit', target: 'save-edit', message: 'Pulsa Guardar cambios para conservar la edición.' },
  { id: 'note-active', target: 'note-active', message: 'Perfecto. La nota ya aparece activa en la ficha del cliente.' },
  { id: 'row-clock', target: 'row-clock', message: 'Pulsa aquí para configurar los horarios de este cliente.' },
  { id: 'schedule-arrival', target: 'schedule-arrival', message: 'Indica la hora de llegada del cliente.' },
  { id: 'schedule-departure', target: 'schedule-departure', message: 'Indica la hora de salida del cliente.' },
  { id: 'schedule-days', target: 'schedule-days', message: 'Selecciona los días a los que se aplicará este horario.' },
  { id: 'schedule-week', target: 'schedule-week', message: 'Actívalo si quieres que este horario se aplique solo esta semana.' },
  { id: 'save-schedule', target: 'save-schedule', message: 'Pulsa Guardar cambios para conservar el horario.' },
  { id: 'schedule-active', target: 'schedule-active', message: 'Perfecto. Los horarios ya aparecen visibles en la ficha del cliente.' },
  { id: 'blue-action', target: 'blue-action', message: 'Pulsa sobre el cliente para marcarlo como realizado.' },
  { id: 'blue-active', target: 'blue-active', message: 'Perfecto. El cliente queda marcado en azul como realizado.' },
  { id: 'remove-blue', target: 'remove-blue', message: 'Pulsa de nuevo sobre el cliente para quitar la marca azul.' },
  { id: 'red-action', target: 'red-action', message: 'Pulsa la X para marcar este cliente como anulado.' },
  { id: 'red-active', target: 'red-active', message: 'El cliente queda marcado en rojo como anulado.' },
  { id: 'remove-red', target: 'remove-red', message: 'Pulsa de nuevo la X para quitar la anulación.' },
  { id: 'delete-entry', target: 'delete-entry', message: 'Pulsa Editar para acceder a las opciones del cliente.' },
  { id: 'delete-action', target: 'delete-action', message: 'Pulsa aquí para eliminar este cliente.' },
  { id: 'delete-confirm', target: 'delete-confirm', message: 'Pulsa "Sí, borrar" para eliminar el cliente.' },
  { id: 'delete-result', target: 'delete-result', message: 'El cliente se ha eliminado correctamente.' },
  { id: 'tutorial-complete', target: 'tutorial-complete', message: 'Has completado Primeros pasos.' },
];

export function FirstStepsTutorial({ onExit, completionActionLabel, onCompletionAction }) {
  const [today] = useState(buildToday);
  const [client, setClient] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [draft, setDraft] = useState(() => makeInitialDraft(today.name));
  const [error, setError] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editDraft, setEditDraft] = useState({
    informativeNote: '',
    noteDays: [],
    noteOnlyThisWeek: false,
    canceledDays: [],
  });
  const [cancelWeekOffset, setCancelWeekOffset] = useState(0);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleDraft, setScheduleDraft] = useState({
    arrivalTime: '',
    departureTime: '',
    days: [],
    onlyThisWeek: false,
  });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletedClient, setDeletedClient] = useState(null);

  const resetScenario = () => {
    setClient(null);
    setFormOpen(false);
    setDraft(makeInitialDraft(today.name));
    setError(null);
    setEditOpen(false);
    setEditDraft({ informativeNote: '', noteDays: [], noteOnlyThisWeek: false, canceledDays: [] });
    setCancelWeekOffset(0);
    setScheduleOpen(false);
    setScheduleDraft({ arrivalTime: '', departureTime: '', days: [], onlyThisWeek: false });
    setDeleteConfirmOpen(false);
    setDeletedClient(null);
  };

  const handleBack = (targetStep) => {
    if (!targetStep) return;

    switch (targetStep.id) {
      // Alta — formulario acumulativo
      case 'plus':
        setFormOpen(false);
        setDraft(makeInitialDraft(today.name));
        break;
      case 'name':
        setDraft(makeInitialDraft(today.name));
        break;
      case 'whatsapp':
        setDraft((prev) => ({ ...prev, whatsapp: '', locationUrl: '', count: '1', isFixed: true, days: [today.name] }));
        break;
      case 'maps':
        setDraft((prev) => ({ ...prev, locationUrl: '', count: '1', isFixed: true, days: [today.name] }));
        break;
      case 'count':
        setDraft((prev) => ({ ...prev, count: '1', isFixed: true, days: [today.name] }));
        break;
      case 'fixed':
        setDraft((prev) => ({ ...prev, isFixed: true, days: [today.name] }));
        break;
      case 'days':
        setDraft((prev) => ({ ...prev, days: [today.name] }));
        break;
      case 'save':
        setFormOpen(true);
        break;
      case 'result':
      case 'row-maps':
      case 'row-whatsapp':
        break;

      // Edición — editDraft acumulativo
      case 'row-edit':
        setEditOpen(false);
        break;
      case 'edit-data':
        break;
      case 'note-text':
        setEditDraft((prev) => ({ ...prev, informativeNote: '', noteDays: [], noteOnlyThisWeek: false, canceledDays: [] }));
        break;
      case 'note-days':
        setEditDraft((prev) => ({ ...prev, noteDays: [], noteOnlyThisWeek: false, canceledDays: [] }));
        break;
      case 'note-week':
        setEditDraft((prev) => ({ ...prev, noteOnlyThisWeek: false, canceledDays: [] }));
        break;
      case 'cancel':
        setCancelWeekOffset(0);
        setEditDraft((prev) => ({ ...prev, canceledDays: [] }));
        break;
      case 'cancel-navigation':
        setCancelWeekOffset(0);
        break;
      case 'save-edit':
        setEditOpen(true);
        break;
      case 'note-active':
        break;

      // Horario — scheduleDraft acumulativo
      case 'row-clock':
        setScheduleOpen(false);
        break;
      case 'schedule-arrival':
        setScheduleDraft((prev) => ({ ...prev, arrivalTime: '', departureTime: '', days: [], onlyThisWeek: false }));
        break;
      case 'schedule-departure':
        setScheduleDraft((prev) => ({ ...prev, departureTime: '', days: [], onlyThisWeek: false }));
        break;
      case 'schedule-days':
        setScheduleDraft((prev) => ({ ...prev, days: [], onlyThisWeek: false }));
        break;
      case 'schedule-week':
        setScheduleDraft((prev) => ({ ...prev, onlyThisWeek: false }));
        break;
      case 'save-schedule':
        setScheduleOpen(true);
        break;
      case 'schedule-active':
        break;

      // Estado azul
      case 'blue-action':
        setClient((prev) => (prev ? { ...prev, dailyState: 'normal' } : prev));
        break;
      case 'blue-active':
        setClient((prev) => (prev ? { ...prev, dailyState: 'completed' } : prev));
        break;
      case 'remove-blue':
        setClient((prev) => (prev ? { ...prev, dailyState: 'completed' } : prev));
        break;

      // Estado rojo
      case 'red-action':
        setClient((prev) => (prev ? { ...prev, dailyState: 'normal' } : prev));
        break;
      case 'red-active':
        setClient((prev) => (prev ? { ...prev, dailyState: 'urgent' } : prev));
        break;
      case 'remove-red':
        setClient((prev) => (prev ? { ...prev, dailyState: 'urgent' } : prev));
        break;

      // Borrado
      case 'delete-entry':
        setEditOpen(false);
        break;
      case 'delete-action':
        setDeleteConfirmOpen(false);
        break;
      case 'delete-confirm':
        if (deletedClient) {
          setClient(deletedClient);
        }
        setDeleteConfirmOpen(true);
        break;
      case 'delete-result':
      case 'tutorial-complete':
        break;

      default:
        break;
    }
  };

  const updateDraft = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const toggleDay = (day) => {
    setDraft((prev) => {
      const hasDay = prev.days.includes(day);
      return {
        ...prev,
        days: hasDay ? prev.days.filter((d) => d !== day) : [...prev.days, day],
      };
    });
  };

  const updateEditDraft = (field, value) => {
    setEditDraft((prev) => ({ ...prev, [field]: value }));
  };

  const toggleNoteDay = (dayName) => {
    setEditDraft((prev) => {
      const has = prev.noteDays.includes(dayName);
      return {
        ...prev,
        noteDays: has ? prev.noteDays.filter((d) => d !== dayName) : [...prev.noteDays, dayName],
      };
    });
  };

  const toggleCancelDay = (dateKey) => {
    setEditDraft((prev) => {
      const has = prev.canceledDays.includes(dateKey);
      return {
        ...prev,
        canceledDays: has ? prev.canceledDays.filter((d) => d !== dateKey) : [...prev.canceledDays, dateKey],
      };
    });
  };

  const changeCancelWeek = (delta) => {
    setCancelWeekOffset((prev) => prev + delta);
  };

  const resetCanceledDays = () => {
    setEditDraft((prev) => ({ ...prev, canceledDays: [] }));
  };

  const updateScheduleDraft = (field, value) => {
    setScheduleDraft((prev) => ({ ...prev, [field]: value }));
  };

  const toggleScheduleDay = (dayName) => {
    setScheduleDraft((prev) => {
      const has = prev.days.includes(dayName);
      return {
        ...prev,
        days: has ? prev.days.filter((d) => d !== dayName) : [...prev.days, dayName],
      };
    });
  };

  const saveClient = () => {
    if (!draft.name.trim()) {
      setError('El nombre del perro no puede estar vacío.');
      return false;
    }
    if (draft.days.length === 0) {
      setError('Debes seleccionar al menos un día.');
      return false;
    }
    setClient({
      name: draft.name.trim(),
      whatsapp: draft.whatsapp.trim(),
      locationUrl: draft.locationUrl.trim(),
      count: parseInt(draft.count, 10) || 1,
      isFixed: draft.isFixed,
      days: draft.days,
      informativeNote: '',
      noteDays: [],
      noteOnlyThisWeek: false,
      canceledDays: [],
      schedule: { arrivalTime: '', departureTime: '', days: [], onlyThisWeek: false },
      dailyState: 'normal',
    });
    setFormOpen(false);
    setError(null);
    return true;
  };

  const renderStep = (step, index, actions) => {
    const goNext = actions?.goNext ?? (() => {});
    const restart = actions?.restart ?? (() => {});
    const exit = actions?.exit ?? (() => {});
    const completeAction = onCompletionAction ?? exit;
    const completeLabel = completionActionLabel ?? 'Volver a Tutoriales';

    const handleOpenForm = () => {
      setError(null);
      setFormOpen(true);
      goNext();
    };

    const handleContinue = () => {
      goNext();
    };

    const handleSkip = (field) => {
      setDraft((prev) => ({ ...prev, [field]: '' }));
      setError(null);
      goNext();
    };

    const handleSave = () => {
      if (saveClient()) {
        goNext();
      }
    };

    const handleOpenEdit = () => {
      setEditDraft({
        informativeNote: client?.informativeNote || '',
        noteDays: client?.noteDays || [],
        noteOnlyThisWeek: client?.noteOnlyThisWeek || false,
        canceledDays: client?.canceledDays || [],
      });
      setCancelWeekOffset(0);
      setEditOpen(true);
      goNext();
    };

    const handleSaveEdit = () => {
      setClient((prev) => ({
        ...prev,
        informativeNote: editDraft.informativeNote,
        noteDays: editDraft.noteDays,
        noteOnlyThisWeek: editDraft.noteOnlyThisWeek,
        canceledDays: editDraft.canceledDays,
      }));
      setEditOpen(false);
      setError(null);
      goNext();
    };

    const handleChangeCancelWeek = (delta) => {
      changeCancelWeek(delta);
      goNext();
    };

    const handleOpenSchedule = () => {
      setScheduleDraft({
        arrivalTime: client?.schedule?.arrivalTime || '',
        departureTime: client?.schedule?.departureTime || '',
        days: client?.schedule?.days || [],
        onlyThisWeek: client?.schedule?.onlyThisWeek || false,
      });
      setScheduleOpen(true);
      goNext();
    };

    const handleSaveSchedule = () => {
      setClient((prev) => ({
        ...prev,
        schedule: {
          arrivalTime: scheduleDraft.arrivalTime,
          departureTime: scheduleDraft.departureTime,
          days: scheduleDraft.days,
          onlyThisWeek: scheduleDraft.onlyThisWeek,
        },
      }));
      setScheduleOpen(false);
      setError(null);
      goNext();
    };

    const handleToggleBlue = () => {
      setClient((prev) => ({
        ...prev,
        dailyState: prev?.dailyState === 'completed' ? 'normal' : 'completed',
      }));
      goNext();
    };

    const handleToggleRed = () => {
      setClient((prev) => ({
        ...prev,
        dailyState: prev?.dailyState === 'urgent' ? 'normal' : 'urgent',
      }));
      goNext();
    };

    const handleOpenDelete = () => {
      setDeleteConfirmOpen(true);
      goNext();
    };

    const handleConfirmDelete = () => {
      setDeletedClient(client);
      setClient(null);
      setEditOpen(false);
      setDeleteConfirmOpen(false);
      setError(null);
      goNext();
    };

    let message = step.message;
    if (step.id === 'row-maps' && !client?.locationUrl) {
      message = 'En Rutapp este icono solo aparece cuando el cliente tiene una localización guardada. Al pulsarlo tendrás acceso directo a Google Maps.';
    }
    if (step.id === 'row-whatsapp' && !client?.whatsapp) {
      message = 'En Rutapp este icono solo aparece cuando el cliente tiene un WhatsApp o grupo guardado. Al pulsarlo tendrás acceso directo.';
    }

    return (
      <SimulatedScenario
        activeTarget={step.target}
        message={message}
        today={today}
        client={client}
        formOpen={formOpen}
        editOpen={editOpen}
        deleteConfirmOpen={deleteConfirmOpen}
        draft={draft}
        editDraft={editDraft}
        cancelWeekOffset={cancelWeekOffset}
        error={error}
        onOpenForm={handleOpenForm}
        onContinue={handleContinue}
        onSkip={handleSkip}
        onSave={handleSave}
        onUpdateDraft={updateDraft}
        onToggleDay={toggleDay}
        onOpenEdit={handleOpenEdit}
        onSaveEdit={handleSaveEdit}
        onUpdateEditDraft={updateEditDraft}
        onToggleNoteDay={toggleNoteDay}
        onToggleCancelDay={toggleCancelDay}
        onChangeCancelWeek={handleChangeCancelWeek}
        onResetCanceledDays={resetCanceledDays}
        scheduleOpen={scheduleOpen}
        scheduleDraft={scheduleDraft}
        onOpenSchedule={handleOpenSchedule}
        onSaveSchedule={handleSaveSchedule}
        onUpdateScheduleDraft={updateScheduleDraft}
        onToggleScheduleDay={toggleScheduleDay}
        onToggleBlue={handleToggleBlue}
        onToggleRed={handleToggleRed}
        onOpenDelete={handleOpenDelete}
        onConfirmDelete={handleConfirmDelete}
        onRestartTutorial={restart}
        onExitTutorial={completeAction}
        exitLabel={completeLabel}
      />
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <DialogHeader>
        <DialogTitle>Primeros pasos</DialogTitle>
        <DialogDescription>
          Aprende a crear tu primer cliente en un entorno de práctica.
        </DialogDescription>
      </DialogHeader>

      <TutorialEngine
        steps={STEPS}
        renderStep={renderStep}
        onExit={onExit}
        onRestart={resetScenario}
        onBack={handleBack}
        showStepNavigation={false}
      />
    </div>
  );
}

function CoachMark({ text, className }) {
  return (
    <div
      className={cn(
        'max-w-[240px] rounded-md border border-primary/40 bg-primary/10 px-2 py-1.5 text-xs font-medium text-foreground shadow-sm',
        className
      )}
    >
      {text}
    </div>
  );
}

function SimulatedScenario({
  activeTarget,
  message,
  today,
  client,
  formOpen,
  editOpen,
  deleteConfirmOpen,
  draft,
  editDraft,
  cancelWeekOffset,
  error,
  onOpenForm,
  onContinue,
  onSkip,
  onSave,
  onUpdateDraft,
  onToggleDay,
  onOpenEdit,
  onSaveEdit,
  onUpdateEditDraft,
  onToggleNoteDay,
  onToggleCancelDay,
  onChangeCancelWeek,
  onResetCanceledDays,
  scheduleOpen,
  scheduleDraft,
  onOpenSchedule,
  onSaveSchedule,
  onUpdateScheduleDraft,
  onToggleScheduleDay,
  onToggleBlue,
  onToggleRed,
  onOpenDelete,
  onConfirmDelete,
  onRestartTutorial,
  onExitTutorial,
  exitLabel,
}) {
  if (deleteConfirmOpen) {
    return (
      <SimulatedDeleteConfirm
        activeTarget={activeTarget}
        message={message}
        client={client}
        onConfirmDelete={onConfirmDelete}
      />
    );
  }

  if (formOpen) {
    return (
      <SimulatedForm
        activeTarget={activeTarget}
        message={message}
        draft={draft}
        error={error}
        onContinue={onContinue}
        onSkip={onSkip}
        onSave={onSave}
        onUpdateDraft={onUpdateDraft}
        onToggleDay={onToggleDay}
      />
    );
  }

  if (editOpen) {
    return (
      <SimulatedEditForm
        activeTarget={activeTarget}
        message={message}
        today={today}
        client={client}
        editDraft={editDraft}
        cancelWeekOffset={cancelWeekOffset}
        error={error}
        onContinue={onContinue}
        onOpenDelete={onOpenDelete}
        onSaveEdit={onSaveEdit}
        onUpdateEditDraft={onUpdateEditDraft}
        onToggleNoteDay={onToggleNoteDay}
        onToggleCancelDay={onToggleCancelDay}
        onChangeCancelWeek={onChangeCancelWeek}
        onResetCanceledDays={onResetCanceledDays}
      />
    );
  }

  if (scheduleOpen) {
    return (
      <SimulatedScheduleForm
        activeTarget={activeTarget}
        message={message}
        client={client}
        scheduleDraft={scheduleDraft}
        onContinue={onContinue}
        onSaveSchedule={onSaveSchedule}
        onUpdateScheduleDraft={onUpdateScheduleDraft}
        onToggleScheduleDay={onToggleScheduleDay}
      />
    );
  }

  if (activeTarget === 'tutorial-complete') {
    return (
      <SimulatedComplete
        onRestartTutorial={onRestartTutorial}
        onExitTutorial={onExitTutorial}
        exitLabel={exitLabel}
      />
    );
  }

  return (
    <SimulatedScreen
      activeTarget={activeTarget}
      message={message}
      today={today}
      client={client}
      onOpenForm={onOpenForm}
      onOpenEdit={onOpenEdit}
      onOpenSchedule={onOpenSchedule}
      onToggleBlue={onToggleBlue}
      onToggleRed={onToggleRed}
      onContinue={onContinue}
    />
  );
}

function SimulatedScreen({ activeTarget, message, today, client, onOpenForm, onOpenEdit, onOpenSchedule, onToggleBlue, onToggleRed, onContinue }) {
  const plusActive = activeTarget === 'plus';
  const deleteResult = activeTarget === 'delete-result';
  const hasClient = !!client;
  const dim = 'opacity-40';

  return (
    <div className="overflow-hidden rounded-lg border bg-background">
      <div className="flex items-center justify-between border-b px-3 py-2">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-200">
          <BookOpen className="h-3.5 w-3.5" />
          Entorno de práctica
        </span>
        <span className="text-xs font-medium text-muted-foreground">{today.name}</span>
      </div>

      <div className={cn('flex items-center justify-center gap-4 py-2', dim)}>
        <ChevronLeft className="h-4 w-4 text-primary" />
        <p className="text-sm font-semibold text-foreground">{today.dateLabel}</p>
        <ChevronRight className="h-4 w-4 text-primary" />
      </div>

      <div className={cn('flex items-center justify-center gap-1 px-3 pb-2', dim)}>
        {DISPLAY_WEEK_DAYS.map((d) => (
          <span
            key={d.name}
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-full text-xs font-medium',
              d.name === today.name
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground'
            )}
          >
            {d.letter}
          </span>
        ))}
      </div>

      <div className="flex items-center justify-center gap-1 px-3 pb-3">
        <span className={cn('flex h-9 w-9 items-center justify-center rounded-full bg-muted text-muted-foreground', dim)}>
          <Check className="h-4 w-4" />
        </span>
        <span className={cn('flex h-9 w-9 items-center justify-center rounded-full bg-muted text-muted-foreground', dim)}>
          <Undo2 className="h-4 w-4" />
        </span>
        <span className={cn('flex h-9 w-9 items-center justify-center rounded-full bg-muted text-muted-foreground', dim)}>
          <Redo2 className="h-4 w-4" />
        </span>

        <div className="ml-1 flex flex-col items-center">
          {plusActive && <CoachMark text={message} className="mb-2" />}
          <button
            type="button"
            onClick={onOpenForm}
            aria-label="Añadir cliente"
            className={cn(
              'flex h-10 w-10 items-center justify-center rounded-full border bg-muted border-primary shadow-md',
              plusActive
                ? 'ring-2 ring-primary ring-offset-2'
                : 'pointer-events-none opacity-40'
            )}
          >
            <Plus className="h-[23px] w-[23px] text-primary" strokeWidth={3} />
          </button>
        </div>
      </div>

      <div className={cn('min-h-32 border-t px-3 py-3', !hasClient && !deleteResult && dim)}>
        <p className="text-xs font-medium text-muted-foreground">Clientes</p>
        {hasClient ? (
          <ClientRow
            client={client}
            activeTarget={activeTarget}
            message={message}
            onOpenEdit={onOpenEdit}
            onOpenSchedule={onOpenSchedule}
            onToggleBlue={onToggleBlue}
            onToggleRed={onToggleRed}
            onContinue={onContinue}
          />
        ) : (
          <div className="mt-2">
            {deleteResult && <CoachMark text={message} className="mb-2" />}
            <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border px-3 py-6">
              <p className="text-sm text-muted-foreground">Sin clientes todavía</p>
              {deleteResult && (
                <Button type="button" size="sm" onClick={onContinue}>Continuar</Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function fieldState(field, activeTarget) {
  if (activeTarget === 'save') return 'completed';
  const activeIdx = FIELD_ORDER.indexOf(activeTarget);
  const fieldIdx = FIELD_ORDER.indexOf(field);
  if (activeIdx === -1) return 'locked';
  if (fieldIdx < activeIdx) return 'completed';
  if (fieldIdx === activeIdx) return 'active';
  return 'locked';
}

function FieldWrapper({ state, children }) {
  return (
    <div
      className={cn(
        'grid gap-1.5',
        state === 'locked' && 'pointer-events-none opacity-40',
        state === 'active' && 'rounded-md border border-primary/40 bg-primary/5 p-2'
      )}
    >
      {children}
    </div>
  );
}

function SimulatedForm({
  activeTarget,
  message,
  draft,
  error,
  onContinue,
  onSkip,
  onSave,
  onUpdateDraft,
  onToggleDay,
}) {
  return (
    <div className="rounded-lg border bg-background">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <h3 className="text-base font-semibold">Añadir Nuevo Cliente</h3>
        <span className="text-xs font-medium uppercase text-amber-600 dark:text-amber-300">
          Práctica
        </span>
      </div>

      <div className="max-h-[52vh] overflow-y-auto p-4">
        <div className="grid gap-4">
          <FieldWrapper state={fieldState('name', activeTarget)}>
            {activeTarget === 'name' && <CoachMark text={message} className="mb-1" />}
            <Label htmlFor="tut-dog-name" className="text-muted-foreground">Nombre del Cliente</Label>
            <Input id="tut-dog-name" value={draft.name} onChange={(e) => onUpdateDraft('name', e.target.value)} disabled={fieldState('name', activeTarget) === 'locked'} className="bg-background border-border" />
            {activeTarget === 'name' && (
              <div>
                <Button type="button" size="sm" disabled={!draft.name.trim()} onClick={onContinue}>Continuar</Button>
              </div>
            )}
          </FieldWrapper>

          <FieldWrapper state={fieldState('whatsapp', activeTarget)}>
            {activeTarget === 'whatsapp' && <CoachMark text={message} className="mb-1" />}
            <Label htmlFor="tut-dog-whatsapp" className="text-muted-foreground">WhatsApp (Opcional)</Label>
            <Input id="tut-dog-whatsapp" value={draft.whatsapp} onChange={(e) => onUpdateDraft('whatsapp', e.target.value)} placeholder="Enlace del grupo de WhatsApp" disabled={fieldState('whatsapp', activeTarget) === 'locked'} className="bg-background border-border" />
            {activeTarget === 'whatsapp' && (
              <div className="flex gap-2">
                <Button type="button" size="sm" onClick={onContinue}>Continuar</Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => onSkip('whatsapp')}>Omitir</Button>
              </div>
            )}
          </FieldWrapper>

          <FieldWrapper state={fieldState('maps', activeTarget)}>
            {activeTarget === 'maps' && <CoachMark text={message} className="mb-1" />}
            <Label htmlFor="tut-dog-location" className="text-muted-foreground">Localización de Google Maps (Opcional)</Label>
            <Input id="tut-dog-location" value={draft.locationUrl} onChange={(e) => onUpdateDraft('locationUrl', e.target.value)} placeholder="URL de Google Maps" disabled={fieldState('maps', activeTarget) === 'locked'} className="bg-background border-border" />
            {activeTarget === 'maps' && (
              <div className="flex gap-2">
                <Button type="button" size="sm" onClick={onContinue}>Continuar</Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => onSkip('locationUrl')}>Omitir</Button>
              </div>
            )}
          </FieldWrapper>

          <FieldWrapper state={fieldState('count', activeTarget)}>
            {activeTarget === 'count' && <CoachMark text={message} className="mb-1" />}
            <Label htmlFor="tut-dog-count" className="text-muted-foreground">Cantidad</Label>
            <Input id="tut-dog-count" type="number" min="1" value={draft.count} onChange={(e) => onUpdateDraft('count', e.target.value)} disabled={fieldState('count', activeTarget) === 'locked'} className="bg-background border-border" />
            {activeTarget === 'count' && (
              <div>
                <Button type="button" size="sm" onClick={onContinue}>Continuar</Button>
              </div>
            )}
          </FieldWrapper>

          <FieldWrapper state={fieldState('fixed', activeTarget)}>
            {activeTarget === 'fixed' && <CoachMark text={message} className="mb-1" />}
            <div className="flex items-center space-x-2">
              <Switch id="tut-dog-fixed" checked={draft.isFixed} onCheckedChange={(checked) => onUpdateDraft('isFixed', checked)} disabled={fieldState('fixed', activeTarget) === 'locked'} />
              <Label htmlFor="tut-dog-fixed" className="text-muted-foreground flex items-center">
                {draft.isFixed ? <Pin className="mr-2 h-4 w-4 text-primary" /> : <PinOff className="mr-2 h-4 w-4" />}
                Cliente fijo (se repite semanalmente)
              </Label>
            </div>
            {activeTarget === 'fixed' && (
              <div>
                <Button type="button" size="sm" onClick={onContinue}>Continuar</Button>
              </div>
            )}
          </FieldWrapper>

          <FieldWrapper state={fieldState('days', activeTarget)}>
            {activeTarget === 'days' && <CoachMark text={message} className="mb-1" />}
            <Label className="text-muted-foreground">Días del Cliente</Label>
            <div className="mt-2 grid grid-cols-4 gap-2">
              {DISPLAY_WEEK_DAYS.map((day) => {
                const isSelected = draft.days.includes(day.name);
                return (
                  <Button
                    key={day.name}
                    type="button"
                    onClick={() => onToggleDay(day.name)}
                    disabled={fieldState('days', activeTarget) === 'locked'}
                    className={cn(
                      'w-full h-auto py-2 border transition-colors',
                      isSelected
                        ? 'bg-primary text-primary-foreground border-primary hover:bg-primary/90'
                        : 'bg-card text-foreground border-border hover:bg-muted'
                    )}
                  >
                    <span className="text-xs">{day.letter}</span>
                  </Button>
                );
              })}
            </div>
            {activeTarget === 'days' && (
              <div>
                <Button type="button" size="sm" disabled={draft.days.length === 0} onClick={onContinue}>Continuar</Button>
              </div>
            )}
          </FieldWrapper>

          {error && (
            <p className="text-sm font-medium text-red-600 dark:text-red-400">{error}</p>
          )}
        </div>
      </div>

      <div className={cn('flex items-center justify-end gap-2 border-t px-4 py-3', activeTarget === 'save' && 'rounded-md ring-2 ring-primary/60')}>
        {activeTarget === 'save' && <CoachMark text={message} className="mr-auto" />}
        <Button type="button" variant="outline" disabled>Cancelar</Button>
        <Button type="button" onClick={onSave} disabled={activeTarget !== 'save'} className="bg-primary text-primary-foreground hover:bg-primary/90">
          <Plus className="mr-2 h-4 w-4" />
          Añadir Cliente
        </Button>
      </div>
    </div>
  );
}

const EDIT_ORDER = ['edit-data', 'note-text', 'note-days', 'note-week', 'cancel', 'cancel-navigation', 'save-edit'];

function editSectionState(section, activeTarget) {
  const activeIdx = EDIT_ORDER.indexOf(activeTarget);
  const sectionIdx = EDIT_ORDER.indexOf(section);
  if (activeIdx === -1) return 'locked';
  if (sectionIdx < activeIdx) return 'completed';
  if (sectionIdx === activeIdx) return 'active';
  return 'locked';
}

function EditSection({ state, children }) {
  return (
    <div
      className={cn(
        'rounded-lg border p-3',
        state === 'locked' && 'pointer-events-none opacity-40',
        state === 'active' && 'border-primary/40 bg-primary/5'
      )}
    >
      {children}
    </div>
  );
}

function ReadOnlyField({ label, value }) {
  return (
    <div className="grid gap-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-foreground">
        {value || <span className="text-muted-foreground">—</span>}
      </div>
    </div>
  );
}

function ActionButton({ active, onClick, ariaLabel, className, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className={cn(
        'flex h-8 w-8 items-center justify-center rounded-md',
        className,
        active ? 'ring-2 ring-primary' : 'pointer-events-none opacity-40'
      )}
    >
      {children}
    </button>
  );
}

function ClientRow({ client, activeTarget, message, onOpenEdit, onOpenSchedule, onToggleBlue, onToggleRed, onContinue }) {
  const rowActive = activeTarget === 'list';
  const rowAction = activeTarget === 'row-maps' || activeTarget === 'row-whatsapp' || activeTarget === 'row-edit';
  const noteActive = activeTarget === 'note-active';
  const clockActive = activeTarget === 'row-clock';
  const scheduleActive = activeTarget === 'schedule-active';
  const blueAction = activeTarget === 'blue-action';
  const blueActive = activeTarget === 'blue-active';
  const removeBlue = activeTarget === 'remove-blue';
  const redAction = activeTarget === 'red-action';
  const redActive = activeTarget === 'red-active';
  const removeRed = activeTarget === 'remove-red';
  const deleteEntry = activeTarget === 'delete-entry';
  const isCompleted = client.dailyState === 'completed';
  const isUrgent = client.dailyState === 'urgent';

  return (
    <div className="mt-2">
      {(rowActive || rowAction || deleteEntry || noteActive || clockActive || scheduleActive || blueAction || blueActive || removeBlue || redAction || redActive || removeRed) && <CoachMark text={message} className="mb-2" />}

      <div className={cn(
        'rounded-xl border bg-card px-3 py-2',
        isCompleted ? 'bg-primary/40 border-primary' : isUrgent ? 'bg-red-500/20 border-border' : 'border-border',
        rowActive && 'ring-2 ring-primary/60'
      )}>

      <div className="flex items-center gap-2">
        <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground opacity-40" />

        <div
          className={cn('flex min-w-0 flex-1 flex-col', (blueAction || removeBlue) && 'cursor-pointer rounded-md p-1 ring-2 ring-primary')}
          onClick={(blueAction || removeBlue) ? onToggleBlue : undefined}
        >
          <span className="text-sm font-semibold text-foreground">{client.name}</span>
          {client.informativeNote?.trim() ? (
            <span className={cn('mt-1 inline-flex items-center gap-1 self-start rounded-lg bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200', noteActive && 'ring-2 ring-primary')}>
              {client.informativeNote}
            </span>
          ) : null}
        </div>

        <div className="flex shrink-0 items-center gap-1">
          <ActionButton active={activeTarget === 'row-maps'} onClick={onContinue} ariaLabel="Abrir ubicación en Google Maps" className="text-[hsl(var(--gmaps))]">
            <MapPin className="h-4 w-4" />
          </ActionButton>
          <ActionButton active={activeTarget === 'row-whatsapp'} onClick={onContinue} ariaLabel="Abrir chat de WhatsApp" className="text-green-600">
            <MessageCircle className="h-4 w-4" />
          </ActionButton>
          <ActionButton active={activeTarget === 'row-edit' || activeTarget === 'delete-entry'} onClick={onOpenEdit} ariaLabel="Editar cliente" className="text-blue-600">
            <Edit className="h-4 w-4" />
          </ActionButton>

          <button
            type="button"
            onClick={onToggleRed}
            aria-label="Marcar como urgente"
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-md',
              isUrgent ? 'bg-red-500 text-white' : 'text-red-600',
              (redAction || removeRed) ? 'ring-2 ring-primary' : 'pointer-events-none opacity-40'
            )}
          >
            <X className="h-4 w-4" />
          </button>

          <ActionButton active={activeTarget === 'row-clock'} onClick={onOpenSchedule} ariaLabel="Ver horarios" className="text-muted-foreground">
            <Clock className="h-4 w-4" />
          </ActionButton>
        </div>
      </div>

      {(client.schedule?.arrivalTime || client.schedule?.departureTime) ? (
        <div className={cn('mt-1 flex items-center justify-end gap-3 px-1 pb-1', scheduleActive && 'rounded-md ring-2 ring-primary')}>
          {client.schedule.arrivalTime && (
            <span className="inline-flex items-center gap-1 font-mono text-xs text-muted-foreground">
              <PlayCircle className="h-3.5 w-3.5" />
              {client.schedule.arrivalTime}
            </span>
          )}
          {client.schedule.departureTime && (
            <span className="inline-flex items-center gap-1 font-mono text-xs text-muted-foreground">
              <Flag className="h-3.5 w-3.5" />
              {client.schedule.departureTime}
            </span>
          )}
        </div>
      ) : null}
      </div>

      {(rowActive || noteActive || scheduleActive || blueActive || redActive) && (
        <div className="mt-2">
          <Button type="button" size="sm" onClick={onContinue}>Continuar</Button>
        </div>
      )}
    </div>
  );
}

function SimulatedEditForm({
  activeTarget,
  message,
  today,
  client,
  editDraft,
  cancelWeekOffset,
  error,
  onContinue,
  onOpenDelete,
  onSaveEdit,
  onUpdateEditDraft,
  onToggleNoteDay,
  onToggleCancelDay,
  onChangeCancelWeek,
  onResetCanceledDays,
}) {
  const sec = (s) => editSectionState(s, activeTarget);
  const deleteAction = activeTarget === 'delete-action';

  const anchor = new Date(mondayOf(today.date));
  anchor.setDate(anchor.getDate() + cancelWeekOffset * 7);
  const weekDates = getWeekDates(anchor);

  return (
    <div className="rounded-lg border bg-background">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <h3 className="text-base font-semibold">Editar cliente</h3>
        <span className="text-xs font-medium uppercase text-amber-600 dark:text-amber-300">Práctica</span>
      </div>

      <div className="max-h-[52vh] overflow-y-auto p-4">
        <div className="grid gap-4">
          <EditSection state={sec('edit-data')}>
            {activeTarget === 'edit-data' && <CoachMark text={message} className="mb-2" />}
            <h4 className="mb-2 text-sm font-semibold text-muted-foreground">Datos guardados</h4>
            <div className="grid gap-3">
              <ReadOnlyField label="Nombre del cliente" value={client?.name} />
              <ReadOnlyField label="WhatsApp del cliente" value={client?.whatsapp} />
              <ReadOnlyField label="Localización de Google Maps" value={client?.locationUrl} />
              <ReadOnlyField label="Número de plazas" value={client?.count != null ? String(client.count) : ''} />
              <div className="grid gap-1">
                <Label className="text-xs text-muted-foreground">Días de actividad</Label>
                <div className="flex flex-wrap gap-1">
                  {(client?.days || []).map((d) => (
                    <span key={d} className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">{d}</span>
                  ))}
                </div>
              </div>
            </div>
            {activeTarget === 'edit-data' && (
              <div className="mt-2"><Button type="button" size="sm" onClick={onContinue}>Continuar</Button></div>
            )}
          </EditSection>

          <EditSection state={sec('note-text')}>
            <h4 className="mb-2 text-sm font-semibold">Nota informativa</h4>
            {activeTarget === 'note-text' && <CoachMark text={message} className="mb-2" />}
            <Input
              value={editDraft.informativeNote}
              onChange={(e) => onUpdateEditDraft('informativeNote', e.target.value)}
              placeholder="Escribe una nota..."
              disabled={sec('note-text') === 'locked'}
              className="bg-background border-border"
            />
            {activeTarget === 'note-text' && (
              <div className="mt-2">
                <p className="text-xs text-muted-foreground">Por ejemplo: Cancelado</p>
                <div className="mt-1"><Button type="button" size="sm" disabled={editDraft.informativeNote.trim() === ''} onClick={onContinue}>Continuar</Button></div>
              </div>
            )}
          </EditSection>

          <EditSection state={sec('note-days')}>
            {activeTarget === 'note-days' && <CoachMark text={message} className="mb-2" />}
            <Label className="text-muted-foreground">Días de la nota</Label>
            <div className="mt-1 grid grid-cols-7 gap-1">
              {DISPLAY_WEEK_DAYS.map((day) => {
                const isSelected = editDraft.noteDays.includes(day.name);
                return (
                  <Button key={day.name} type="button" onClick={() => onToggleNoteDay(day.name)} disabled={sec('note-days') === 'locked'}
                    className={cn('h-auto py-1.5 border', isSelected ? 'bg-primary text-primary-foreground border-primary' : 'bg-card text-foreground border-border hover:bg-muted')}>
                    <span className="text-xs">{day.letter}</span>
                  </Button>
                );
              })}
            </div>
            {activeTarget === 'note-days' && (
              <div className="mt-2"><Button type="button" size="sm" disabled={editDraft.noteDays.length === 0} onClick={onContinue}>Continuar</Button></div>
            )}
          </EditSection>

          <EditSection state={sec('note-week')}>
            {activeTarget === 'note-week' && <CoachMark text={message} className="mb-2" />}
            <div className="flex items-center space-x-2">
              <Switch id="tut-note-week" checked={editDraft.noteOnlyThisWeek} onCheckedChange={(v) => onUpdateEditDraft('noteOnlyThisWeek', v)} disabled={sec('note-week') === 'locked'} />
              <Label htmlFor="tut-note-week" className="text-muted-foreground">Solo esta semana</Label>
            </div>
            {activeTarget === 'note-week' && (
              <div className="mt-2"><Button type="button" size="sm" onClick={onContinue}>Continuar</Button></div>
            )}
          </EditSection>

          <EditSection state={sec('cancel')}>
            <h4 className="mb-2 text-sm font-semibold">Días de cancelación</h4>
            {activeTarget === 'cancel' && <CoachMark text={message} className="mb-2" />}

            {activeTarget === 'cancel-navigation' && <CoachMark text={message} className="mb-2" />}
            <div className={cn('flex items-center justify-between gap-2', sec('cancel-navigation') === 'locked' && 'pointer-events-none opacity-40')}>
              <Button type="button" variant="outline" size="sm" onClick={() => onChangeCancelWeek(-1)} disabled={sec('cancel-navigation') === 'locked'}>← Anterior</Button>
              <span className="text-xs text-muted-foreground">
                {weekDates[0].toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })} - {weekDates[6].toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
              </span>
              <Button type="button" variant="outline" size="sm" onClick={() => onChangeCancelWeek(1)} disabled={sec('cancel-navigation') === 'locked'}>Siguiente →</Button>
            </div>
            {activeTarget === 'cancel-navigation' && (
              <div className="mt-2"><Button type="button" size="sm" onClick={onContinue}>Continuar</Button></div>
            )}

            <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
              {weekDates.map((date) => {
                const dateKey = formatDateKey(date);
                const dayName = WEEK_DAYS[date.getDay()].name;
                const hasActivity = (client?.days || []).includes(dayName);
                const isCanceled = editDraft.canceledDays.includes(dateKey);
                const locked = sec('cancel') === 'locked';
                return (
                  <button
                    key={dateKey}
                    type="button"
                    disabled={!hasActivity || locked}
                    onClick={() => onToggleCancelDay(dateKey)}
                    className={cn(
                      'flex h-auto flex-col items-center rounded-md border py-2 transition-colors',
                      !hasActivity
                        ? 'cursor-not-allowed bg-muted text-muted-foreground opacity-50'
                        : isCanceled
                        ? 'bg-red-500/30 text-red-700 dark:text-red-400 border-red-500 hover:bg-red-500/40'
                        : 'bg-card text-foreground border-border hover:bg-muted',
                      locked && 'pointer-events-none opacity-40'
                    )}
                  >
                    <span className="text-xs font-medium">{dayName}</span>
                    <span className="text-xs opacity-75">{date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}</span>
                  </button>
                );
              })}
            </div>

            {activeTarget === 'cancel' && (
              <div className="mt-2"><Button type="button" size="sm" disabled={editDraft.canceledDays.length === 0} onClick={onContinue}>Continuar</Button></div>
            )}

            <div className="mt-2">
              <Button type="button" variant="outline" size="sm" onClick={onResetCanceledDays} disabled={sec('cancel') === 'locked'} className="w-full text-xs">
                Restablecer días cancelados
              </Button>
              <p className="mt-1 text-xs text-muted-foreground">Restablecer elimina los días cancelados seleccionados.</p>
            </div>
          </EditSection>

          {error && <p className="text-sm font-medium text-red-600 dark:text-red-400">{error}</p>}
        </div>
      </div>

      <div className={cn('flex items-center justify-end gap-2 border-t px-4 py-3', activeTarget === 'save-edit' && 'rounded-md ring-2 ring-primary/60')}>
        {activeTarget === 'save-edit' && <CoachMark text={message} className="mr-auto" />}
        {deleteAction && <CoachMark text={message} className="mr-auto" />}
        <button
          type="button"
          onClick={onOpenDelete}
          aria-label="Eliminar cliente"
          title="Eliminar cliente"
          disabled={!deleteAction}
          className={cn(
            'flex h-9 w-9 items-center justify-center rounded-md border bg-muted text-destructive',
            deleteAction ? 'ring-2 ring-destructive ring-offset-2' : 'pointer-events-none opacity-40'
          )}
        >
          <Trash2 className="h-4 w-4" />
        </button>
        <Button type="button" variant="outline" disabled>Cancelar</Button>
        <Button type="button" onClick={onSaveEdit} disabled={activeTarget !== 'save-edit'} className="bg-primary text-primary-foreground hover:bg-primary/90">
          Guardar cambios
        </Button>
      </div>
    </div>
  );
}

const SCHEDULE_ORDER = ['schedule-arrival', 'schedule-departure', 'schedule-days', 'schedule-week', 'save-schedule'];

function scheduleSectionState(section, activeTarget) {
  const activeIdx = SCHEDULE_ORDER.indexOf(activeTarget);
  const sectionIdx = SCHEDULE_ORDER.indexOf(section);
  if (activeIdx === -1) return 'locked';
  if (sectionIdx < activeIdx) return 'completed';
  if (sectionIdx === activeIdx) return 'active';
  return 'locked';
}

function ScheduleSection({ state, children }) {
  return (
    <div
      className={cn(
        'rounded-md',
        state === 'locked' && 'pointer-events-none opacity-40',
        state === 'active' && 'rounded-lg border border-primary/40 bg-primary/5 p-2'
      )}
    >
      {children}
    </div>
  );
}

function SimulatedScheduleForm({
  activeTarget,
  message,
  client,
  scheduleDraft,
  onContinue,
  onSaveSchedule,
  onUpdateScheduleDraft,
  onToggleScheduleDay,
}) {
  const sec = (s) => scheduleSectionState(s, activeTarget);

  return (
    <div className="rounded-lg border bg-background">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <h3 className="text-base font-semibold">Horarios de {client?.name}</h3>
        <span className="text-xs font-medium uppercase text-amber-600 dark:text-amber-300">Práctica</span>
      </div>

      <div className="max-h-[52vh] overflow-y-auto p-4">
        <div className="rounded-lg border border-primary/40 bg-card p-3">
          <p className="text-sm font-semibold">Nuevo horario</p>

          <div className="mt-3 grid gap-4">
            <ScheduleSection state={sec('schedule-arrival')}>
              {activeTarget === 'schedule-arrival' && <CoachMark text={message} className="mb-2" />}
              <Label htmlFor="tut-arrival" className="text-muted-foreground">Hora de llegada</Label>
              <Input id="tut-arrival" type="time" value={scheduleDraft.arrivalTime} onChange={(e) => onUpdateScheduleDraft('arrivalTime', e.target.value)} disabled={sec('schedule-arrival') === 'locked'} className="bg-background border-border" />
              {activeTarget === 'schedule-arrival' && (
                <div className="mt-1"><Button type="button" size="sm" onClick={onContinue}>Continuar</Button></div>
              )}
            </ScheduleSection>

            <ScheduleSection state={sec('schedule-departure')}>
              {activeTarget === 'schedule-departure' && <CoachMark text={message} className="mb-2" />}
              <Label htmlFor="tut-departure" className="text-muted-foreground">Hora de salida</Label>
              <Input id="tut-departure" type="time" value={scheduleDraft.departureTime} onChange={(e) => onUpdateScheduleDraft('departureTime', e.target.value)} disabled={sec('schedule-departure') === 'locked'} className="bg-background border-border" />
              {activeTarget === 'schedule-departure' && (
                <div className="mt-1"><Button type="button" size="sm" onClick={onContinue}>Continuar</Button></div>
              )}
            </ScheduleSection>

            <ScheduleSection state={sec('schedule-days')}>
              {activeTarget === 'schedule-days' && <CoachMark text={message} className="mb-2" />}
              <Label className="text-muted-foreground">Días</Label>
              <div className="mt-1 grid grid-cols-7 gap-1">
                {DISPLAY_WEEK_DAYS.map((day) => {
                  const isSelected = scheduleDraft.days.includes(day.name);
                  return (
                    <Button key={day.name} type="button" onClick={() => onToggleScheduleDay(day.name)} disabled={sec('schedule-days') === 'locked'}
                      className={cn('h-auto py-1.5 border', isSelected ? 'bg-primary text-primary-foreground border-primary' : 'bg-card text-foreground border-border hover:bg-muted')}>
                      <span className="text-xs">{day.letter}</span>
                    </Button>
                  );
                })}
              </div>
              {activeTarget === 'schedule-days' && (
                <div className="mt-1"><Button type="button" size="sm" disabled={scheduleDraft.days.length === 0} onClick={onContinue}>Continuar</Button></div>
              )}
            </ScheduleSection>

            <ScheduleSection state={sec('schedule-week')}>
              {activeTarget === 'schedule-week' && <CoachMark text={message} className="mb-2" />}
              <div className="flex items-center justify-between gap-2">
                <div className="space-y-0.5">
                  <Label className="text-xs font-medium">Solo esta semana</Label>
                  <p className="text-xs text-muted-foreground">Este horario no se repetirá en semanas futuras.</p>
                </div>
                <Switch id="tut-schedule-week" checked={scheduleDraft.onlyThisWeek} onCheckedChange={(v) => onUpdateScheduleDraft('onlyThisWeek', v)} disabled={sec('schedule-week') === 'locked'} />
              </div>
              {activeTarget === 'schedule-week' && (
                <div className="mt-1"><Button type="button" size="sm" onClick={onContinue}>Continuar</Button></div>
              )}
            </ScheduleSection>
          </div>
        </div>
      </div>

      <div className={cn('flex items-center justify-end gap-2 border-t px-4 py-3', activeTarget === 'save-schedule' && 'rounded-md ring-2 ring-primary/60')}>
        {activeTarget === 'save-schedule' && <CoachMark text={message} className="mr-auto" />}
        <Button type="button" variant="outline" disabled>Cerrar sin guardar</Button>
        <Button type="button" onClick={onSaveSchedule} disabled={activeTarget !== 'save-schedule'} className="bg-primary text-primary-foreground hover:bg-primary/90">
          Guardar cambios
        </Button>
      </div>
    </div>
  );
}

function SimulatedDeleteConfirm({ activeTarget, message, client, onConfirmDelete }) {
  const deleteConfirmActive = activeTarget === 'delete-confirm';
  return (
    <div className="rounded-lg border bg-background">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <h3 className="text-base font-semibold">Eliminar cliente</h3>
        <span className="text-xs font-medium uppercase text-amber-600 dark:text-amber-300">Práctica</span>
      </div>
      <div className="p-4">
        {deleteConfirmActive && <CoachMark text={message} className="mb-3" />}
        <p className="text-sm text-muted-foreground">
          ¿Estás seguro de que quieres borrar a {client?.name} de la app?
        </p>
        <div className="mt-4 flex items-center justify-end gap-2">
          <Button type="button" variant="outline" disabled>Cancelar</Button>
          <Button type="button" variant="destructive" onClick={onConfirmDelete}>Sí, borrar</Button>
        </div>
      </div>
    </div>
  );
}

function SimulatedComplete({ onRestartTutorial, onExitTutorial, exitLabel = 'Volver a Tutoriales' }) {
  return (
    <div className="rounded-lg border bg-background p-6 text-center">
      <h3 className="text-lg font-semibold">Has completado Primeros pasos.</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Ya sabes crear y gestionar un cliente en Rutapp.
      </p>
      <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-center">
        <Button type="button" variant="outline" onClick={onRestartTutorial}>Repetir tutorial</Button>
        <Button type="button" onClick={onExitTutorial}>{exitLabel}</Button>
      </div>
    </div>
  );
}
